/**
 * PaymentSearchPanel — Phase 4 Payments (G.20 follow-up).
 *
 * Hannah, Sep 18 2026: Stripe emails the firm's Intake inbox for every payment, including ones from
 * people who aren't the client on file, and there was no way to look one up and see which case it
 * belonged to. This searches by the Stripe payment link id, a staff reference, an amount, or the
 * client/case it's already on, and gives a direct "Open case" link for each match.
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Stack,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import PaymentApi, {
  PaymentSearchResultDto,
  isDeadPayment,
  paymentMethodLabel,
  paymentStatusColor,
  paymentStatusLabel,
} from '../../api/PaymentApi';
import { formatCentsUsd } from '../../constants/paymentPresets';

const PaymentSearchPanel: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PaymentSearchResultDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runSearch = async () => {
    if (!query.trim()) {
      setResults(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setResults(await PaymentApi.searchPayments(query));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search payments');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="section">
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Find a payment
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Search a Stripe payment link id, a staff reference, an amount, or a client name to find which case a payment belongs to.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Stripe ID, reference, amount, or client name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') runSearch();
          }}
          sx={{ minWidth: 320, flex: 1 }}
        />
        <Button variant="contained" startIcon={<SearchIcon />} onClick={runSearch} disabled={loading}>
          Search
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {results && !loading && (
        results.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No payments matched "{query}".
          </Typography>
        ) : (
          <Stack spacing={1}>
            {results.map((r) => {
              const dead = isDeadPayment(r.TicketPayment_Status);
              return (
                <Card key={r.TicketPayment_guid} variant="outlined" sx={dead ? { opacity: 0.6 } : undefined}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCentsUsd(r.TicketPayment_AmountCents)}
                      </Typography>
                      <Chip
                        label={paymentStatusLabel(r.TicketPayment_Status)}
                        size="small"
                        variant="outlined"
                        color={paymentStatusColor(r.TicketPayment_Status)}
                      />
                      <Chip label={paymentMethodLabel(r.TicketPayment_Method)} size="small" variant="outlined" />
                      <Box sx={{ flex: 1 }} />
                      <Button
                        size="small"
                        onClick={() => navigate(`/ticket?Ticket_guid=${r.Ticket_guid}`)}
                        disabled={!r.Ticket_guid}
                      >
                        Open case
                      </Button>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {r.clientName || 'Unknown client'}
                      {r.Ticket_MatterNumber || r.Ticket_Number
                        ? ` — ${r.Ticket_MatterNumber || r.Ticket_Number}`
                        : ''}
                      {r.Ticket_Citation ? ` (${r.Ticket_Citation})` : ''}
                    </Typography>
                    {(r.TicketPayment_Reference || r.TicketPayment_StripePaymentLinkId) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                        {r.TicketPayment_Reference ? `Ref: ${r.TicketPayment_Reference}` : ''}
                        {r.TicketPayment_Reference && r.TicketPayment_StripePaymentLinkId ? ' · ' : ''}
                        {r.TicketPayment_StripePaymentLinkId ? `Stripe: ${r.TicketPayment_StripePaymentLinkId}` : ''}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )
      )}
    </Box>
  );
};

export default PaymentSearchPanel;
